import type { ReactNode } from 'react';
import type { Twiggy } from './core/client';
import type {
  EventProperties,
  IdentifyData,
  ProductViewData,
  AddToCartData,
  RemoveFromCartData,
  CheckoutStepData,
  SearchData
} from './types';


let React: typeof import('react') | null = null;

try {
  React = require('react');
} catch {
  
}

interface TwiggyContextValue {
  client: Twiggy;
}

interface TwiggyProviderProps {
  client: Twiggy;
  children: ReactNode;
}


const TwiggyContext = React ? React.createContext<TwiggyContextValue | null>(null) : null;


export function TwiggyProvider({ client, children }: TwiggyProviderProps): ReactNode {
  if (!React || !TwiggyContext) {
    console.warn('Twiggy: React is required for TwiggyProvider');
    return children;
  }

  const value = React.useMemo(() => ({ client }), [client]);

  return React.createElement(TwiggyContext.Provider, { value }, children);
}


export function useTwiggy(): Twiggy {
  if (!React || !TwiggyContext) {
    throw new Error('Twiggy: React is required for useTwiggy hook');
  }

  const context = React.useContext(TwiggyContext);

  if (!context) {
    throw new Error('Twiggy: useTwiggy must be used within a TwiggyProvider');
  }

  return context.client;
}


export function useTracking() {
  const client = useTwiggy();

  if (!React) {
    throw new Error('Twiggy: React is required for useTracking hook');
  }

  return React.useMemo(
    () => ({
      
      track: (eventName: string, properties?: EventProperties) => {
        client.track(eventName, properties);
      },

      
      pageView: (properties?: EventProperties) => {
        client.pageView(properties);
      },

      
      productView: (data: ProductViewData) => {
        client.productView(data);
      },

      
      addToCart: (data: AddToCartData) => {
        client.addToCart(data);
      },

      
      removeFromCart: (data: RemoveFromCartData) => {
        client.removeFromCart(data);
      },

      
      checkoutStep: (data: CheckoutStepData) => {
        client.checkoutStep(data);
      },

      
      search: (data: SearchData) => {
        client.search(data);
      },

      
      identify: (data: IdentifyData) => {
        return client.identify(data);
      },

      
      getVisitorId: () => {
        return client.getVisitorId();
      },

      
      getSessionId: () => {
        return client.getSessionId();
      },

      
      flush: () => {
        return client.flush();
      },

      
      reset: () => {
        client.reset();
      }
    }),
    [client]
  );
}


export function usePageTracking(dependencies: unknown[] = []): void {
  const { pageView } = useTracking();

  if (!React) {
    console.warn('Twiggy: React is required for usePageTracking hook');
    return;
  }

  React.useEffect(() => {
    pageView();
    
  }, dependencies);
}


export function useProductTracking(product: ProductViewData | null): void {
  const { productView } = useTracking();

  if (!React) {
    console.warn('Twiggy: React is required for useProductTracking hook');
    return;
  }

  React.useEffect(() => {
    if (product) {
      productView(product);
    }
  }, [product?.productId]);
}
